import { z } from "zod";
import { albumSchema } from "@libs/catalog-engine-contracts/album";
import { artistSchema } from "./artist";
import { playlistSchema } from "./playlist";

/**
 * Wire shape of `user.listFavourites`. Each variant extends the catalog
 * collection schema (album / artist / playlist) with the discriminator
 * fields the procedure adds at the top level (`collectionId` +
 * `collectionType`).
 *
 * Co-located with the procedure so the client durable-cache layer
 * (`apps/mobile/hooks/trpc/useFavourites/queryFunctions.ts`) can import
 * the same source-of-truth schema and parse cached rows on read. A drift
 * assertion in that file binds the schema to the tRPC return type at
 * compile time so any divergence trips a TS error.
 */
export const favouritesAlbumSchema = z.object({
  collectionId: z.string(),
  collectionType: z.literal("album"),
  ...albumSchema.shape,
});

export const favouritesArtistSchema = z.object({
  collectionId: z.string(),
  collectionType: z.literal("artist"),
  ...artistSchema.shape,
});

export const favouritesPlaylistSchema = z.object({
  collectionId: z.string(),
  collectionType: z.literal("playlist"),
  ...playlistSchema.shape,
});

export const favouritesListItemSchema = z.discriminatedUnion("collectionType", [
  favouritesAlbumSchema,
  favouritesArtistSchema,
  favouritesPlaylistSchema,
]);

export const favouritesListSchema = z.array(favouritesListItemSchema);

export type FavouritesListItem = z.infer<typeof favouritesListItemSchema>;
export type FavouritesList = z.infer<typeof favouritesListSchema>;
